import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import * as Sentry from '@sentry/react';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { i18nReady } from './i18n';
import { installNumericSelectOnFocus } from './lib/selectOnFocus';
import './index.css';

// Initialize Sentry error tracking
Sentry.init({
  dsn: 'https://a50e36a801a7e10f44181020fd91845d@o4511145964666880.ingest.us.sentry.io/4511145970827264',
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  environment: import.meta.env.VITE_ENVIRONMENT === 'staging' ? 'staging' : 'production',
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  enabled: import.meta.env.PROD,
  ignoreErrors: [
    // Jam.dev capture.js references SharedWorker without feature detection;
    // throws on Chrome Mobile Android where SharedWorker is not defined.
    'SharedWorker is not defined',
  ],
});

// Defer the larger integrations until the first screen has rendered.
const loadSentryIntegrations = () => {
  if (!import.meta.env.PROD) return;
  Sentry.lazyLoadIntegration('replayIntegration').then((replayIntegration) => {
    Sentry.addIntegration(replayIntegration());
  }).catch(() => { /* replay is optional */ });
  void Sentry.lazyLoadIntegration('feedbackIntegration').then((feedbackIntegration) => {
    Sentry.addIntegration(feedbackIntegration({
      colorScheme: 'light',
      showBranding: false,
      autoInject: false,
    }));
  }).catch(() => { /* feedback is optional */ });
};

const loadJam = () => {
  for (const src of ['https://js.jam.dev/recorder.js', 'https://js.jam.dev/capture.js']) {
    const script = document.createElement('script');
    script.type = 'module';
    script.async = false;
    script.src = src;
    document.head.appendChild(script);
  }
};

let idleScriptsScheduled = false;
function IdleScripts() {
  React.useEffect(() => {
    if (idleScriptsScheduled) return;
    idleScriptsScheduled = true;
    const load = () => { loadSentryIntegrations(); loadJam(); };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load);
    } else {
      setTimeout(load, 0);
    }
  }, []);
  return null;
}

// Configure AWS Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// A tab opened before a deploy asks for page chunks that no longer exist:
// reload once to pick up the new build instead of showing an error.
// Once a reload is underway, every further chunk error in this page lifetime
// (another page, HelpPanel, a CSS dep) is part of the same reload.
let reloading = false;
window.addEventListener('vite:preloadError', (event) => {
  Sentry.addBreadcrumb({ category: 'chunk', level: 'warning', message: 'vite:preloadError', data: { reloading } });
  if (reloading) { event.preventDefault(); return; }
  const key = 'arivo:chunkReloadAt';
  let last = 0;
  try { last = Number(sessionStorage.getItem(key)) || 0; } catch { /* storage unavailable */ }
  if (Date.now() - last < 10_000) return; // already reloaded just now: let the error show
  try { sessionStorage.setItem(key, String(Date.now())); } catch { /* storage unavailable */ }
  event.preventDefault();
  reloading = true;
  window.location.reload();
});

installNumericSelectOnFocus();

// Render even if the language file failed to load (keys beat a blank page).
void i18nReady.catch(() => undefined).then(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
        <IdleScripts />
      </QueryClientProvider>
    </React.StrictMode>
  );
});
// Build 1769523226
